import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';

import {
  downloadReportPdf,
  filterParametersSelector,
  masterDataDataSelector,
  reportFilterAccountingEntriesFieldsSelector,
  reportFilterCountryCodeSelector,
  reportFilterGroupBySelector,
  reportMissingClosingTotalMissingClosingTotalSelector,
  reportSalesSelector,
  reportSalesTypesSelector,
} from '../../state';
import { Sale } from '../../types';
import handlerFactory, { SalesTotalsDataGridContext } from './handlerFactory';

const DataGridTitle = () => {
  const { t } = useTranslation();
  const { loaded, data: totals } = useSelector(
    reportMissingClosingTotalMissingClosingTotalSelector
  );
  let title = t('sale-totals.title');

  if (loaded && totals !== null) {
    title += ` (${totals} ${t('sale-totals.labels.missing')})`;
  }
  return <span>{title}</span>;
};

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<Sale, SalesTotalsDataGridContext>();
  const { t } = useTranslation();
  const titles = useMemo(
    () => ({ title: t('sale-totals.title'), tableTitle: <DataGridTitle /> }),
    [t]
  );

  const country = useSelector(reportFilterCountryCodeSelector);
  const salesResource = useSelector(reportSalesSelector);
  const groupBy = useSelector(reportFilterGroupBySelector);
  const masterData = useSelector(masterDataDataSelector);
  const appParameters = useSelector(filterParametersSelector);
  const accountingEntriesFields = useSelector(reportFilterAccountingEntriesFieldsSelector);
  const { data: saleTypeOptions } = useSelector(reportSalesTypesSelector);
  const storeExtraInfo = useMemo(
    () => appParameters.data?.storeExtraInfo ?? [],
    [appParameters.data?.storeExtraInfo]
  );

  const { loading, data } = salesResource;

  const {
    agreements = [],
    companies = [],
    managements = [],
    regionalManagements = [],
    regions = [],
    supervisions = [],
  } = masterData || {};

  const gridContext = useMemo(
    () => ({
      t,
      country,
      groupBy,
      saleTypeOptions,
      agreements,
      companies,
      managements,
      regionalManagements,
      regions,
      supervisions,
      storeExtraInfo,
      accountingEntriesFields,
    }),
    [
      t,
      country,
      groupBy,
      saleTypeOptions,
      agreements,
      companies,
      managements,
      regionalManagements,
      regions,
      supervisions,
      storeExtraInfo,
      accountingEntriesFields,
    ]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title: titles,
    loading,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<Sale, SalesTotalsDataGridContext>();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: [
                'countryCode',
                'stores',
                'pos',
                'salesTypes',
                'segments',
                'timeBand',
                'hourRange',
                'startAccountingDate',
                'endAccountingDate',
                'startOperationDate',
                'endOperationDate',
                'startBusinessDate',
                'endBusinessDate',
                'groupBy',
              ],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t]);

  return null;
};

const SalesTotalsDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default SalesTotalsDataGrid;
